import React, {
    createContext, useReducer, useMemo, useContext, useEffect, useCallback, useRef
} from 'react';
import posthog from 'posthog-js';
import { FarmsContext } from './Farms';
import config from '../config';
import useLazyGetData from '../customHooks/useLazyGetData';
import useSubmit from '../customHooks/useSubmit';
import { GET_ACTIVE_FARM_SETTING, SAVE_ACTIVE_FARM_SETTING } from '../queries/activeFarmSetting.query';

const initialState = {
    activeFarm: null,
    activeFarmTimeZone: null,
};

const actions = {
    SET_ACTIVE_FARM: 'SET_ACTIVE_FARM'
};

function reducer(state, action) {
    switch (action.type) {
        case actions.SET_ACTIVE_FARM:
            return { ...state, activeFarm: action.farmId, activeFarmTimeZone: action.timeZone };
        default:
            return state;
    }
}

export const ActiveFarmContext = createContext();

const ActiveFarmContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { farms } = useContext(FarmsContext);
    const initialSyncDone = useRef(false);
    const isSettingFarm = useRef(false);
    const hasStartedLoading = useRef(false);

    const [fetchActiveFarmSetting, {
        data: apiFarmData, loading: apiLoading, error: apiError
    }] = useLazyGetData(GET_ACTIVE_FARM_SETTING, null, true);
    const [saveActiveFarmMutation] = useSubmit(SAVE_ACTIVE_FARM_SETTING);

    const applyFarm = useCallback((farmId, timeZone) => {
        if (!farmId || Number(farmId) <= 0) return;
        dispatch({ type: actions.SET_ACTIVE_FARM, farmId, timeZone });
        localStorage.setItem('activeFarmId', farmId);
        localStorage.setItem('activeFarmTimeZone', timeZone);
    }, []);

    const findFarmById = useCallback(
        (farmId) => farms?.find(f => f?.id?.toString() === farmId?.toString()),
        [farms],
    );

    // Trigger initial fetch when farms are loaded
    useEffect(() => {
        if (farms?.length) {
            fetchActiveFarmSetting();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farms]);

    // Track when loading has actually started (to avoid acting on the initial false)
    useEffect(() => {
        if (apiLoading) {
            hasStartedLoading.current = true;
        }
    }, [apiLoading]);

    // Process API response reactively (handles both initial load and visibility-change refetch)
    useEffect(() => {
        if (!farms?.length || apiLoading || !hasStartedLoading.current || isSettingFarm.current) return;

        const apiFarmId = apiFarmData?.saved_settings?.farmId;

        if (apiFarmId) {
            const matchedFarm = findFarmById(apiFarmId);
            if (matchedFarm) {
                if (matchedFarm.id.toString() !== state.activeFarm?.toString()) {
                    applyFarm(matchedFarm.id, matchedFarm.timeZone);
                }
                initialSyncDone.current = true;
                return;
            }
        }

        // API returned null, returned unknown farm, or errored — fall back (only on initial sync)
        if (!initialSyncDone.current) {
            const localFarmId = localStorage.getItem('activeFarmId');
            const matchedLocal = findFarmById(localFarmId);

            if (matchedLocal) {
                applyFarm(matchedLocal.id, matchedLocal.timeZone);
                saveActiveFarmMutation({ farmId: +matchedLocal.id }).catch(() => {});
            } else {
                applyFarm(farms[0].id, farms[0].timeZone);
                saveActiveFarmMutation({ farmId: +farms[0].id }).catch(() => {});
            }
            initialSyncDone.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiFarmData, apiLoading, apiError]);

    // Cross-device sync: re-fetch from API when the tab regains focus
    useEffect(() => {
        if (farms?.length) {
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible' && !isSettingFarm.current) {
                    fetchActiveFarmSetting();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farms]);

    useEffect(() => {
        if (state.activeFarm) {
            posthog.register({
                farm_id: state.activeFarm,
                farm_timezone: state.activeFarmTimeZone,
                platform: window.matchMedia('(display-mode: standalone)').matches ? 'app' : 'web',
                app_version: config.version
            });
        }
    }, [state]);

    const setActiveFarm = useCallback(async (farmId, timeZone) => {
        isSettingFarm.current = true;
        dispatch({ type: actions.SET_ACTIVE_FARM, farmId, timeZone });
        localStorage.setItem('activeFarmId', farmId);
        localStorage.setItem('activeFarmTimeZone', timeZone);

        try {
            await saveActiveFarmMutation({ farmId: +farmId });
        } catch {
            // Mutation failed — local state is already updated
        } finally {
            isSettingFarm.current = false;
        }
    }, [saveActiveFarmMutation]);

    // Update only the timezone of the currently active farm (e.g. after editing it),
    // keeping the same active farm selected.
    const setActiveFarmTimeZone = useCallback((timeZone) => {
        if (!state.activeFarm) return;
        dispatch({ type: actions.SET_ACTIVE_FARM, farmId: state.activeFarm, timeZone });
        localStorage.setItem('activeFarmTimeZone', timeZone);
    }, [state.activeFarm]);

    const value = useMemo(() => ({
        activeFarm: state.activeFarm,
        activeFarmTimeZone: state.activeFarmTimeZone,
        setActiveFarm,
        setActiveFarmTimeZone,
    }), [state, setActiveFarm, setActiveFarmTimeZone]);

    return (
        <ActiveFarmContext.Provider value={value}>
            {children}
        </ActiveFarmContext.Provider>
    );
}

export default ActiveFarmContextProvider;
